'use client'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

import { useLocale } from '@/components/LocaleContext'
import { getMediaUrl } from '@/lib/media'
import type { Page } from '@/payload-types'

type PageSection = NonNullable<Page['sections']>[number]

type PageSidebarNavProps = {
  sections: PageSection[]
  filteredSectionId?: string | null
  onSectionClick?: (sectionId: string) => void
  contentMetrics: { height: number; top: number }
  scrollContainerRef?: React.RefObject<HTMLElement | null>
}

export function PageSidebarNav({
  sections,
  filteredSectionId = null,
  onSectionClick,
  contentMetrics,
  scrollContainerRef,
}: PageSidebarNavProps) {
  const { translatedData } = useLocale()
  const useInternalScroll = Boolean(scrollContainerRef)
  const interactionReadyRef = useRef(false)
  const unlockObserverRef = useRef<(() => void) | null>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const [scrollActiveId, setScrollActiveId] = useState<string | null>(
    sections[0]?.sectionId ?? null,
  )

  useEffect(() => {
    const scrollRoot = scrollContainerRef?.current
    if (!useInternalScroll || !scrollRoot || sections.length === 0) return

    const markerOffset = 32

    const getActiveSection = () => {
      const rootRect = scrollRoot.getBoundingClientRect()
      const marker = rootRect.top + markerOffset
      const atBottom =
        scrollRoot.scrollTop + scrollRoot.clientHeight >= scrollRoot.scrollHeight - 2

      if (atBottom) {
        return sections[sections.length - 1]?.sectionId ?? null
      }

      let activeId = sections[0]?.sectionId ?? null

      for (const section of sections) {
        const el = document.getElementById(section.sectionId)
        if (!el) continue
        if (el.getBoundingClientRect().top <= marker) {
          activeId = section.sectionId
        }
      }

      return activeId
    }

    const onScroll = () => {
      const next = getActiveSection()
      if (!next) return
      setScrollActiveId((prev) => (prev === next ? prev : next))
    }

    onScroll()
    scrollRoot.addEventListener('scroll', onScroll, { passive: true })

    return () => scrollRoot.removeEventListener('scroll', onScroll)
  }, [sections, scrollContainerRef, useInternalScroll])

  useEffect(() => {
    if (useInternalScroll || sections.length === 0) return

    const topOffset = 140
    let rafOne = 0
    let rafTwo = 0
    let unlockTimeout: ReturnType<typeof setTimeout> | null = null

    const getSectionByViewportPosition = (): string | null => {
      const candidates = sections
        .map((section) => ({
          id: section.sectionId,
          el: document.getElementById(section.sectionId),
        }))
        .filter((item): item is { id: string; el: HTMLElement } => Boolean(item.el))

      if (candidates.length === 0) return sections[0]?.sectionId ?? null

      const firstPassed = candidates
        .filter(({ el }) => el.getBoundingClientRect().top <= topOffset)
        .sort((a, b) => b.el.getBoundingClientRect().top - a.el.getBoundingClientRect().top)[0]

      if (firstPassed) return firstPassed.id

      return (
        candidates.sort(
          (a, b) =>
            Math.abs(a.el.getBoundingClientRect().top - topOffset) -
            Math.abs(b.el.getBoundingClientRect().top - topOffset),
        )[0]?.id ??
        sections[0]?.sectionId ??
        null
      )
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!interactionReadyRef.current) return

        const intersecting = entries.filter((entry) => entry.isIntersecting)
        if (intersecting.length === 0) return

        const bestEntry = intersecting.sort((a, b) => {
          if (b.intersectionRatio !== a.intersectionRatio) {
            return b.intersectionRatio - a.intersectionRatio
          }

          const aDistance = Math.abs(a.boundingClientRect.top - topOffset)
          const bDistance = Math.abs(b.boundingClientRect.top - topOffset)
          return aDistance - bDistance
        })[0]

        const nextSectionId = bestEntry?.target.id
        if (!nextSectionId) return
        setScrollActiveId((prev) => (prev === nextSectionId ? prev : nextSectionId))
      },
      { rootMargin: '0px 0px -55% 0px', threshold: [0.1, 0.25, 0.5] },
    )

    sections.forEach((section) => {
      const el = document.getElementById(section.sectionId)
      if (el) observer.observe(el)
    })

    const unlockObserver = () => {
      if (interactionReadyRef.current) return
      interactionReadyRef.current = true
      const currentSection = getSectionByViewportPosition()
      if (currentSection) {
        setScrollActiveId((prev) => (prev === currentSection ? prev : currentSection))
      }
    }
    unlockObserverRef.current = unlockObserver

    const initialSection = getSectionByViewportPosition()
    if (initialSection) {
      setScrollActiveId(initialSection)
    }

    rafOne = requestAnimationFrame(() => {
      rafTwo = requestAnimationFrame(() => {
        interactionReadyRef.current = true
      })
    })
    unlockTimeout = setTimeout(() => {
      interactionReadyRef.current = true
    }, 250)

    return () => {
      observer.disconnect()
      interactionReadyRef.current = false
      unlockObserverRef.current = null
      window.removeEventListener('wheel', unlockObserver)
      window.removeEventListener('touchmove', unlockObserver)
      window.removeEventListener('keydown', unlockObserver)
      if (unlockTimeout) clearTimeout(unlockTimeout)
      cancelAnimationFrame(rafOne)
      cancelAnimationFrame(rafTwo)
    }
  }, [sections, useInternalScroll])

  useEffect(() => {
    if (useInternalScroll || !sidebarRef.current) return

    let animationFrame = 0

    let currentY = 0
    let targetY = 0

    const viewportOffset = 32
    const ease = 0.12

    const updateTarget = () => {
      if (!sidebarRef.current) return

      const sidebarHeight = sidebarRef.current.offsetHeight

      const maxTranslate = Math.max(0, contentMetrics.height - sidebarHeight)

      const scrollY = window.scrollY - contentMetrics.top + viewportOffset

      targetY = Math.max(0, Math.min(scrollY, maxTranslate))
    }

    const animate = () => {
      if (!sidebarRef.current) return

      currentY += (targetY - currentY) * ease

      if (Math.abs(targetY - currentY) < 0.1) {
        currentY = targetY
      }

      sidebarRef.current.style.transform = `translate3d(0, ${currentY}px, 0)`

      animationFrame = requestAnimationFrame(animate)
    }

    const handleScroll = () => {
      updateTarget()
    }

    updateTarget()

    animate()

    window.addEventListener('scroll', handleScroll, {
      passive: true,
    })

    window.addEventListener('resize', handleScroll)

    return () => {
      cancelAnimationFrame(animationFrame)

      window.removeEventListener('scroll', handleScroll)

      window.removeEventListener('resize', handleScroll)
    }
  }, [contentMetrics, useInternalScroll])

  const activeSectionId = useInternalScroll
    ? scrollActiveId
    : (filteredSectionId ?? scrollActiveId)

  const asideClassName = useInternalScroll
    ? 'hidden w-[260px] shrink-0 self-start sticky top-8 lg:block'
    : 'hidden w-[260px] shrink-0 lg:block'

  return (
    <aside
      className={asideClassName}
      style={useInternalScroll ? undefined : { height: contentMetrics.height }}
    >
      <div
        ref={useInternalScroll ? undefined : sidebarRef}
        style={useInternalScroll ? undefined : { willChange: 'transform' }}
        className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm "
      >
        <h2 className="font-bebas-neue mb-5 text-sm uppercase tracking-[1.5px] text-[#4A5565]">
          {translatedData?.common.quick_links ?? 'Quick Links'}
        </h2>
        <nav aria-label="Page sections">
          <ul className="space-y-2">
            {sections?.map((section) => {
              const navIconUrl = getMediaUrl(section.titleIcon)
              const isActive = activeSectionId === section.sectionId
              const sharedClasses = `flex w-full items-center gap-3 rounded-md px-4 py-2.5 text-left rtl:text-right transition-colors ${
                isActive ? 'bg-[#E00000] text-white' : 'text-[#4A5565] hover:bg-neutral-100'
              }`

              return (
                <li key={section.sectionId}>
                  {onSectionClick ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (!useInternalScroll) unlockObserverRef.current?.()
                        onSectionClick(section.sectionId)
                      }}
                      className={sharedClasses}
                      aria-current={isActive ? 'true' : undefined}
                    >
                      {navIconUrl ? (
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center">
                          <Image
                            src={navIconUrl}
                            alt=""
                            width={20}
                            height={20}
                            className={`h-4 w-4 object-contain ${isActive ? 'brightness-0 invert' : ''}`}
                            aria-hidden
                          />
                        </span>
                      ) : null}
                      <span className="font-bebas-neue text-sm uppercase tracking-[1px]">
                        {section.sectionTitle}
                      </span>
                    </button>
                  ) : (
                    <a
                      href={`#${section.sectionId}`}
                      onClick={() => {
                        if (!useInternalScroll) unlockObserverRef.current?.()
                      }}
                      className={sharedClasses}
                      aria-current={isActive ? 'true' : undefined}
                    >
                      {navIconUrl ? (
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center">
                          <Image
                            src={navIconUrl}
                            alt=""
                            width={20}
                            height={20}
                            className={`h-4 w-4 object-contain ${isActive ? 'brightness-0 invert' : ''}`}
                            aria-hidden
                          />
                        </span>
                      ) : null}
                      <span className="font-bebas-neue text-sm uppercase tracking-[1px]">
                        {section.sectionTitle}
                      </span>
                    </a>
                  )}
                </li>
              )
            })}
          </ul>
        </nav>
      </div>
    </aside>
  )
}
